"use client";

import { useEffect, useState } from "react";
import posthog from "posthog-js";
import { createFormLead, newFormLeadId } from "@/services/forms";
import { uploadApplicationFile } from "@/services/applicationUploads";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/Button";
import { TextField } from "@/components/ui/TextField";
import { Stepper } from "@/components/ui/Stepper";
import { ease } from "@/components/forms/constants";
import { ChevronLeftIcon } from "@/components/ui/Icons/ChevronLeftIcon";
import { ChevronRightIcon } from "@/components/ui/Icons/ChevronRightIcon";
import { CheckIcon } from "@/components/ui/Icons/CheckIcon";
import {
  isValidLinkedinHandle,
  LINKEDIN_PROFILE_PREFIX,
  linkedinHandleFromInput,
  linkedinProfileUrl
} from "./validateLinkedinUrl";
import {
  ApplicationFilePicker,
  type PickedApplicationFile
} from "./ApplicationFilePicker";

// ─── Types ────────────────────────────────────────────────────────────────────

type Step = 1 | 2 | 3 | "confirm";

interface ApplicationData {
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  linkedin: string;
  letter: string;
  ai: "yes_partly" | "yes_entirely" | "no" | null;
}

type Errors = Partial<Record<keyof ApplicationData | "cv", string>>;

const DEFAULT: ApplicationData = {
  firstName: "",
  lastName: "",
  email: "",
  role: "",
  linkedin: "",
  letter: "",
  ai: null
};

const AI_OPTION_VALUES: NonNullable<ApplicationData["ai"]>[] = [
  "yes_partly",
  "yes_entirely",
  "no"
];
const AI_OPTION_KEYS = {
  yes_partly: "aiOptionPartly",
  yes_entirely: "aiOptionEntirely",
  no: "aiOptionNo"
} as const;

const LETTER_MAX = 1500;

interface ApplicationFlowProps {
  /**
   * When set, the applicant is applying to a specific role: the role field is
   * hidden and locked to this value, and the submission is tagged as a
   * `job_application` rather than an open application.
   */
  presetRole?: string;
  /** Slug of the job being applied to (stored alongside the submission). */
  jobSlug?: string;
  /** Human-readable role title, shown on the locked-role step. */
  jobTitle?: string;
  /**
   * Called when the user presses "Back" on the first step. If omitted, the
   * back button on step 1 is hidden (used when the form stands alone).
   */
  onExit?: () => void;
}

// ─── ApplicationFlow ────────────────────────────────────────────────────────

export function ApplicationFlow({
  presetRole,
  jobSlug,
  jobTitle,
  onExit
}: ApplicationFlowProps) {
  const t = useTranslations("OpenApplicationForm");
  const [step, setStep] = useState<Step>(1);
  const [direction, setDirection] = useState(1);
  const [data, setData] = useState<ApplicationData>(DEFAULT);
  const [errors, setErrors] = useState<Errors>({});
  const [submitting, setSubmitting] = useState(false);
  const [networkError, setNetworkError] = useState("");
  // The CV is required; the letter can be typed or uploaded instead.
  const [cv, setCv] = useState<PickedApplicationFile | null>(null);
  const [letterFile, setLetterFile] = useState<PickedApplicationFile | null>(
    null
  );

  const isJobApplication = Boolean(presetRole);
  const formDiscriminator = isJobApplication
    ? "job_application"
    : "open_application";

  // Funnel: fire `lead_started` once when the form first mounts.
  useEffect(() => {
    posthog.capture("lead_started", { form: formDiscriminator });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const patch = (updates: Partial<ApplicationData>) =>
    setData((prev) => ({ ...prev, ...updates }));

  const clearErr = (field: keyof Errors) =>
    setErrors((e) => {
      const next = { ...e };
      delete next[field];
      return next;
    });

  // Only the field and a fixed reason code (type/size/content/unsafe) are sent,
  // never the file's name or contents.
  const trackFileRejected = (field: "cv" | "letter_file", reason: string) =>
    posthog.capture("lead_validation_failed", {
      form: formDiscriminator,
      field,
      reason
    });

  const go = (next: Step, back = false) => {
    setDirection(back ? -1 : 1);
    setStep(next);
    setErrors({});
    setNetworkError("");
  };

  // ─── Validation ─────────────────────────────────────────────────────────────

  const validateStep1 = () => {
    const errs: Errors = {};
    if (!data.firstName.trim()) errs.firstName = t("errorRequired");
    if (!data.lastName.trim()) errs.lastName = t("errorRequired");
    if (!data.email.trim()) errs.email = t("errorRequired");
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email.trim()))
      errs.email = t("errorEmail");
    setErrors(errs);
    return !Object.keys(errs).length;
  };

  const validateStep2 = () => {
    const errs: Errors = {};
    if (!isJobApplication && !data.role.trim()) errs.role = t("errorRequired");
    // Optional; when given, just the handle (a pasted link is reduced to it).
    const handle = linkedinHandleFromInput(data.linkedin);
    if (handle && !isValidLinkedinHandle(handle))
      errs.linkedin = t("errorLinkedin");
    setErrors(errs);
    return !Object.keys(errs).length;
  };

  const validateStep3 = () => {
    const errs: Errors = {};
    if (!cv) errs.cv = t("errorCvRequired");
    if (!data.letter.trim() && !letterFile)
      errs.letter = t("errorLetterOrFile");
    if (!data.ai) errs.ai = t("errorAiRequired");
    setErrors(errs);
    return !Object.keys(errs).length;
  };

  // ─── Handlers ───────────────────────────────────────────────────────────────

  const handleStep1Next = () => {
    if (validateStep1()) {
      posthog.capture("lead_step_completed", {
        form: formDiscriminator,
        step: 1
      });
      go(2);
    }
  };

  const handleStep2Next = () => {
    if (validateStep2()) {
      posthog.capture("lead_step_completed", {
        form: formDiscriminator,
        step: 2
      });
      go(3);
    }
  };

  const handleStep3Submit = async () => {
    if (!validateStep3() || submitting) return;
    setSubmitting(true);
    setNetworkError("");
    const minSpinner = new Promise((r) => setTimeout(r, 900));
    try {
      const linkedinHandle = linkedinHandleFromInput(data.linkedin);
      // Files first, under the id the application doc will get, so the
      // backend finds them when the doc's write triggers it.
      const formId = newFormLeadId();
      if (formId) {
        await Promise.all(
          (
            [
              ["cv", cv],
              ["letter", letterFile]
            ] as const
          )
            .filter(([, picked]) => picked)
            .map(([kind, picked]) =>
              uploadApplicationFile({
                formId,
                kind,
                extension: picked!.extension,
                file: picked!.file
              })
            )
        );
      }
      await Promise.all([
        createFormLead(
          {
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email,
            role: presetRole ?? data.role,
            linkedin: linkedinHandle
              ? linkedinProfileUrl(linkedinHandle)
              : undefined,
            letter: data.letter,
            ai: data.ai,
            form: isJobApplication ? "job_application" : "open_application",
            ...(jobSlug ? { jobSlug } : {})
          },
          formId ?? undefined
        ),
        minSpinner
      ]);
      posthog.capture("lead_step_completed", {
        form: formDiscriminator,
        step: 3
      });
      posthog.capture("lead_submitted", {
        form: formDiscriminator,
        ...(jobSlug ? { job_slug: jobSlug } : {})
      });
      go("confirm");
    } catch (err) {
      console.error(err);
      setNetworkError(t("errorRequestFailed"));
    } finally {
      setSubmitting(false);
    }
  };

  const charCount = data.letter.length;

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={String(step)}
        initial={{ opacity: 0, y: direction * 28 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: direction * -28 }}
        transition={{ duration: 0.38, ease }}
      >
        {/* ── Step 1: Who are you? ── */}
        {step === 1 && (
          <div className="max-w-2xl mx-auto">
            <Stepper current={1} total={3} />
            <h2 className="font-display text-4xl md:text-5xl font-bold text-text-primary leading-[1.05] mb-2.5 text-pretty">
              {t("step1Title")}
            </h2>
            <p className="text-base text-text-muted mb-8">{t("step1Sub")}</p>

            <div className="grid grid-cols-2 gap-5 mb-6">
              <TextField
                label={t("labelFirstName")}
                type="text"
                value={data.firstName}
                onChange={(e) => {
                  patch({ firstName: e.target.value });
                  clearErr("firstName");
                }}
                onKeyDown={(e) => e.key === "Enter" && handleStep1Next()}
                placeholder="First"
                autoComplete="given-name"
                autoFocus
                error={errors.firstName}
                className="text-2xl"
              />
              <TextField
                label={t("labelLastName")}
                type="text"
                value={data.lastName}
                onChange={(e) => {
                  patch({ lastName: e.target.value });
                  clearErr("lastName");
                }}
                onKeyDown={(e) => e.key === "Enter" && handleStep1Next()}
                placeholder="Last"
                autoComplete="family-name"
                error={errors.lastName}
                className="text-2xl"
              />
            </div>

            <div className="mb-8">
              <TextField
                label={t("labelEmail")}
                type="email"
                inputMode="email"
                value={data.email}
                onChange={(e) => {
                  patch({ email: e.target.value });
                  clearErr("email");
                }}
                onKeyDown={(e) => e.key === "Enter" && handleStep1Next()}
                placeholder="you@company.com"
                autoComplete="email"
                error={errors.email}
                className="text-2xl"
              />
            </div>

            <div className="flex items-center gap-4">
              {onExit && (
                <Button
                  variant="outlined"
                  onClick={onExit}
                  startIcon={<ChevronLeftIcon />}
                >
                  {t("back")}
                </Button>
              )}
              <Button onClick={handleStep1Next} endIcon={<ChevronRightIcon />}>
                {t("next")}
              </Button>
            </div>
          </div>
        )}

        {/* ── Step 2: Role + LinkedIn ── */}
        {step === 2 && (
          <div className="max-w-2xl mx-auto">
            <Stepper current={2} total={3} />
            <h2 className="font-display text-4xl md:text-5xl font-bold text-text-primary leading-[1.05] mb-2.5 text-pretty">
              {isJobApplication ? t("step2TitleJob") : t("step2Title")}
            </h2>
            <p className="text-base text-text-muted mb-8">
              {isJobApplication ? t("step2SubJob") : t("step2Sub")}
            </p>

            {isJobApplication ? (
              <div className="mb-8 rounded-xl border border-border bg-bg-surface px-5 py-4">
                <p className="text-xs uppercase tracking-widest text-text-muted font-body">
                  {t("applyingForLabel")}
                </p>
                <p className="mt-1 font-display text-lg font-bold text-text-primary">
                  {jobTitle ?? presetRole}
                </p>
              </div>
            ) : (
              <div className="mb-6">
                <TextField
                  label={t("labelRole")}
                  type="text"
                  value={data.role}
                  onChange={(e) => {
                    patch({ role: e.target.value });
                    clearErr("role");
                  }}
                  onKeyDown={(e) => e.key === "Enter" && handleStep2Next()}
                  placeholder={t("placeholderRole")}
                  autoFocus
                  error={errors.role}
                  className="text-2xl"
                />
              </div>
            )}

            <div className="mb-8">
              <TextField
                label={t("labelLinkedin")}
                prefix={LINKEDIN_PROFILE_PREFIX}
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                value={data.linkedin}
                onChange={(e) => {
                  // A whole pasted link becomes just its handle.
                  const value = e.target.value;
                  patch({
                    linkedin: /linkedin\.com\/in\//i.test(value)
                      ? linkedinHandleFromInput(value)
                      : value
                  });
                  clearErr("linkedin");
                }}
                onKeyDown={(e) => e.key === "Enter" && handleStep2Next()}
                placeholder={t("placeholderLinkedin")}
                autoFocus={isJobApplication}
                error={errors.linkedin}
                className="text-2xl"
              />
            </div>

            <div className="flex items-center gap-4">
              <Button
                variant="outlined"
                onClick={() => go(1, true)}
                startIcon={<ChevronLeftIcon />}
              >
                {t("back")}
              </Button>
              <Button onClick={handleStep2Next} endIcon={<ChevronRightIcon />}>
                {t("next")}
              </Button>
            </div>
          </div>
        )}

        {/* ── Step 3: Tell us more ── */}
        {step === 3 && (
          <div className="max-w-2xl mx-auto">
            <Stepper current={3} total={3} />
            <h2 className="font-display text-4xl md:text-5xl font-bold text-text-primary leading-[1.05] mb-2.5 text-pretty">
              {t("step3Title")}
            </h2>
            <p className="text-base text-text-muted mb-8">
              {data.firstName
                ? `${t("step3PersonalizedPrefix", { firstName: data.firstName })} ${t("step3Sub")}`
                : t("step3Sub")}
            </p>

            <ApplicationFilePicker
              label={t("labelCv")}
              hint={t("fileHint")}
              value={cv}
              onChange={(picked) => {
                setCv(picked);
                if (picked) clearErr("cv");
              }}
              error={errors.cv}
              disabled={submitting}
              onReject={(reason) => trackFileRejected("cv", reason)}
            />

            {/* Textarea */}
            <div className="mb-6">
              <label className="block text-sm text-text-body font-body mb-2">
                {t("labelLetter")}
              </label>
              <textarea
                value={data.letter}
                onChange={(e) => {
                  patch({ letter: e.target.value });
                  clearErr("letter");
                }}
                placeholder={
                  letterFile
                    ? t("placeholderLetterWithFile")
                    : t("placeholderLetter")
                }
                maxLength={LETTER_MAX}
                rows={6}
                className={`w-full bg-transparent border-b pb-2 pt-1 text-base text-text-primary placeholder:text-text-muted font-body outline-none resize-none transition-colors duration-200 focus:border-b-2 focus:pb-[7px] focus:border-bubblegum ${errors.letter ? "border-error" : "border-border"}`}
              />
              <div className="flex items-start justify-between mt-1.5 min-h-[20px]">
                {errors.letter ? (
                  <p className="text-xs text-error font-body">
                    {errors.letter}
                  </p>
                ) : (
                  <span />
                )}
                <span
                  className={`text-xs font-body shrink-0 ${
                    charCount >= LETTER_MAX
                      ? "text-error"
                      : charCount > LETTER_MAX * 0.9
                        ? "text-warning"
                        : "text-text-muted"
                  }`}
                >
                  {charCount.toLocaleString("en")} /{" "}
                  {LETTER_MAX.toLocaleString("en")}
                </span>
              </div>
            </div>

            <ApplicationFilePicker
              label={t("labelLetterFile")}
              hint={t("fileHint")}
              value={letterFile}
              onChange={(picked) => {
                setLetterFile(picked);
                if (picked) clearErr("letter");
              }}
              disabled={submitting}
              onReject={(reason) => trackFileRejected("letter_file", reason)}
            />

            {/* AI usage segmented control */}
            <div className="mb-8">
              <p className="text-sm text-text-body font-body mb-3">
                {t("aiLabel")}
              </p>
              <div className="flex rounded-xl border border-border overflow-hidden">
                {AI_OPTION_VALUES.map((value, i) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => {
                      patch({ ai: value });
                      clearErr("ai");
                    }}
                    className={`flex-1 py-3 px-2 text-sm font-semibold font-body text-center leading-snug transition-colors duration-150 ${
                      i > 0 ? "border-l border-border" : ""
                    } ${
                      data.ai === value
                        ? "bg-bubblegum/15 text-bubblegum-text"
                        : "text-text-muted hover:text-text-primary hover:bg-white/[0.03]"
                    }`}
                  >
                    {t(AI_OPTION_KEYS[value])}
                  </button>
                ))}
              </div>
              {errors.ai && (
                <p className="text-xs text-error font-body mt-1.5">
                  {errors.ai}
                </p>
              )}
              <p className="mt-2 text-xs text-text-muted font-body italic">
                {t("aiExplainer")}
              </p>
            </div>

            <div className="flex items-center gap-4">
              <Button
                variant="outlined"
                onClick={() => go(2, true)}
                startIcon={<ChevronLeftIcon />}
                disabled={submitting}
              >
                {t("back")}
              </Button>
              <Button
                onClick={handleStep3Submit}
                disabled={submitting}
                endIcon={
                  submitting ? (
                    <div className="w-[18px] h-[18px] border-2 border-white/25 border-t-white rounded-full animate-spin" />
                  ) : (
                    <ChevronRightIcon />
                  )
                }
              >
                {submitting ? null : t("submit")}
              </Button>
            </div>

            {networkError && (
              <p
                role="alert"
                className="mt-3 text-sm text-error font-body"
                aria-live="polite"
              >
                {networkError}
              </p>
            )}

            <p className="mt-6 text-xs text-text-muted font-body opacity-75 max-w-md leading-relaxed">
              {t("gdprText")}
            </p>
          </div>
        )}

        {/* ── Confirmation ── */}
        {step === "confirm" && (
          <div className=" max-w-2xl mx-auto">
            <div className="w-14 h-14 rounded-full bg-success/15 border border-success/30 flex items-center justify-center text-success mb-6 mx-auto">
              <CheckIcon className="w-6 h-6" />
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-text-primary leading-[1.05] mb-4 text-pretty">
              {data.firstName
                ? t("confirmTitlePersonalized", { firstName: data.firstName })
                : t("confirmTitle")}
            </h2>
            <p className="text-base text-text-body leading-relaxed whitespace-pre-line">
              {t("confirmBody")}
            </p>
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
}
